import React, { useId, useState } from 'react'
import {
  TooltipAlign,
  TooltipPosition,
  TooltipProps,
  TooltipSize,
  TooltipTargetVariant
} from './Tooltip.type'

const HIDDEN_CLASS = 'u-hidden'

/**
 * Capitalizes the first letter so an enum value maps to its ADEL class token
 * (e.g. `top` -> `Top`, `center` -> `Center`).
 */
const capitalize = (value: string): string =>
  value.charAt(0).toUpperCase() + value.slice(1)

/**
 * Builds the ADEL modifier class list for the tooltip overlay.
 *
 * Bottom is the ADEL default and uses the bare `Tooltip--align*` modifiers with
 * no position prefix. All other placements use the
 * `Tooltip--position<Placement>--align*` family. The small variant always
 * renders bottom-centered per ADEL, so position/alignment modifiers are omitted
 * when {@link TooltipSize.SMALL} is used.
 */
const buildTooltipClasses = (
  position: TooltipPosition,
  align: TooltipAlign,
  size: TooltipSize
): string[] => {
  const classes: string[] = ['Tooltip']

  if (size === TooltipSize.SMALL) {
    classes.push('Tooltip--small')
    return classes
  }

  const alignToken = capitalize(align)

  if (position === TooltipPosition.BOTTOM) {
    classes.push(`Tooltip--align${alignToken}`)
  } else {
    classes.push(`Tooltip--position${capitalize(position)}--align${alignToken}`)
  }

  return classes
}

/**
 * ADEL Tooltip — a contextual overlay shown on hover, focus, or touch of a
 * trigger element, for short non-essential text.
 */
export const Tooltip = (props: TooltipProps): React.ReactElement => {
  const {
    children,
    content,
    targetVariant = TooltipTargetVariant.TEXT,
    position = TooltipPosition.BOTTOM,
    align = TooltipAlign.START,
    size = TooltipSize.DEFAULT,
    useLabelledBy = false,
    className = '',
    testId
  } = props

  const id = useId()

  const [isVisible, setIsVisible] = useState(false)

  const show = (): void => setIsVisible(true)
  const hide = (): void => setIsVisible(false)
  const toggle = (event: React.TouchEvent): void => {
    // Prevent the surrounding page from immediately dismissing the tooltip.
    event.stopPropagation()
    setIsVisible(previous => !previous)
  }

  const targetBaseClass =
    targetVariant === TooltipTargetVariant.ICON
      ? 'Tooltip-icon-target'
      : 'Tooltip-target'

  const targetClasses = [targetBaseClass, className].filter(Boolean).join(' ')

  const tooltipClasses = buildTooltipClasses(position, align, size)
  if (!isVisible) tooltipClasses.push(HIDDEN_CLASS)

  const describedBy = useLabelledBy ? undefined : id
  const labelledBy = useLabelledBy ? id : undefined

  // When the target owns its own focusable control (small icon-button pattern),
  // the wrapper must not steal focus or announce itself as a button.
  const targetOwnsInteractiveChild = size === TooltipSize.SMALL

  return (
    <div
      className={targetClasses}
      aria-describedby={describedBy}
      aria-labelledby={labelledBy}
      role={targetOwnsInteractiveChild ? undefined : 'button'}
      tabIndex={targetOwnsInteractiveChild ? undefined : 0}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      onTouchStart={toggle}
      data-testid={testId ? `${testId}-target` : undefined}
    >
      {children}
      <div
        className={tooltipClasses.join(' ')}
        role="tooltip"
        id={id}
        data-testid={testId ? `${testId}` : undefined}
      >
        <div
          className="Tooltip-content Content"
          data-testid={testId ? `${testId}-content` : undefined}
        >
          {content}
        </div>
      </div>
    </div>
  )
}
